import './UpcomingWebinar.scss'

import classNames from 'classnames'
import Anchor from 'lib/components/Anchor/Anchor'
import SubscribeDialog from 'lib/components/SubscribeDialog/SubscribeDialog'
import AdminContext from 'lib/context/AdminContext'
import AuthContext from 'lib/context/AuthContext'
import SubContext from 'lib/context/SubContext'
import { events, track } from 'lib/util/analytics'
import canViewContent from 'lib/util/canViewContent'
import { UPCOMING_WEBINAR_SIGN_UP_ID } from 'lib/util/routing'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import { isPublished } from 'pages/Home/sections/filtering'
import { formatDay, formatTime, I18NEXT_PREFIX, isInPast } from 'pages/Home/UpcomingWebinarsSection/util'
import UpcomingWebinarType from 'pages/Home/UpcomingWebinarType'
import React, { MouseEvent, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import ListItemHeader from '../sections/ListItemHeader'
import PastWebinarIndicator from './admin/PastWebinarIndicator'
import UpcomingWebinarAdminControls from './admin/UpcomingWebinarAdminControls'

export interface UpcomingWebinarProps {
  recordedWebinars: RecordedWebinarType[]
  renderSeriesLink?: boolean
  upcomingWebinar: UpcomingWebinarType
}

export default function UpcomingWebinar(props: UpcomingWebinarProps) {
  const { recordedWebinars, renderSeriesLink, upcomingWebinar } = props
  const { id, date, description, presenter, recordedWebinar, title, registrationLink } = upcomingWebinar
  const [t] = useTranslation()
  const { showAdminControls } = useContext(AdminContext)
  const { getAccessToken, self } = useContext(AuthContext)
  const userSubStatus = useContext(SubContext)
  const [isSubscribeDialogOpen, setSubscribeDialogOpen] = useState(false)
  const history = useHistory()

  const skillLevel = recordedWebinar.skillLevel
  const closeSubscribeDialog = () => setSubscribeDialogOpen(false)
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  function setSignUpParam() {
    const params = new URLSearchParams(history.location.search)
    params.set(UPCOMING_WEBINAR_SIGN_UP_ID, id)
    history.replace({
      ...history.location,
      search: `?${params.toString()}`,
      state: history.location.state
    })
  }

  function onClickSignUp(e: MouseEvent) {
    track(
      events.registrationLinkClicked,
      {
        upcomingWebinarId: id,
        title,
        presenter,
        recordedWebinarId: recordedWebinar.id,
        recordedWebinarTitle: recordedWebinar.title,
        rise: recordedWebinar.rise,
        storyline: recordedWebinar.storyline,
        more: recordedWebinar.more,
        seriesId: recordedWebinar.series.id,
        seriesTitle: recordedWebinar.series.title,
        skillLevel
      },
      getAccessToken,
      showAdminControls
    )

    if (!canViewContent(self, userSubStatus)) {
      e.preventDefault()
      setSubscribeDialogOpen(true)
    }
  }

  const renderAsPast = showAdminControls && isInPast(upcomingWebinar)
  const className = classNames('upcoming-webinar', {
    'upcoming-webinar--unpublished': !isPublished(upcomingWebinar),
    'upcoming-webinar--past': renderAsPast
  })

  return (
    <li className={className}>
      <div className="upcoming-webinar__detail-section">
        <ListItemHeader
          renderSeriesLink={renderSeriesLink}
          series={upcomingWebinar.recordedWebinar.series}
          skillLevel={skillLevel}
        />
        <h3 className="upcoming-webinar__title">{title}</h3>
        <p className="upcoming-webinar__description">{description}</p>
      </div>
      <div className="upcoming-webinar__border" />
      <div className="upcoming-webinar__sign-up-section">
        <div className="upcoming-webinar__sign-up-details">
          <div className="upcoming-webinar__day">{formatDay(localTimeZone, date, t)}</div>
          <div className="upcoming-webinar__time">{formatTime(localTimeZone, date, t)}</div>
          <div className="upcoming-webinar__presenter">{presenter}</div>
        </div>
        <Anchor
          className="upcoming-webinar__sign-up-button"
          target="_blank"
          rel="noopener noreferrer"
          href={renderAsPast ? undefined : registrationLink}
          onClick={onClickSignUp}>
          {t(`${I18NEXT_PREFIX}.signUpButton`)}
        </Anchor>
      </div>
      {renderAsPast && <PastWebinarIndicator />}
      {showAdminControls && (
        <UpcomingWebinarAdminControls recordedWebinars={recordedWebinars} upcomingWebinar={upcomingWebinar} />
      )}
      {isSubscribeDialogOpen && (
        <SubscribeDialog onRequestClose={closeSubscribeDialog} onClickSignIn={setSignUpParam} />
      )}
    </li>
  )
}
